import { type Observable, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import * as dimensionsApi from '~/api/dimensions';
import { SubmissionsApi } from '~/api/submissions';
import type { ApiExportsEntry, ApiTimeDimensionVersion } from '~/types/api-types';

export interface ExportEntry {
  secrecy: boolean;
  id: number;
  filename: string;
  exportType: string;
  version: string;
  createdAt: Date | null;
}

export interface ExportType {
  name: string;
  id: number;
}

export interface ExportsYear {
  year: string;
  exports: ExportEntry[];
}

export interface Version {
  name: string;
  id: number;
}

class ExportServiceClass {
  public exports(): Observable<ExportsYear[]> {
    const mapper = (data: ApiExportsEntry[]): ExportsYear[] =>
      data.map((entry) => ({
        year: entry.year,
        exports: entry.exports.map((exportEntry) => ({
          secrecy: exportEntry.secrecy === 'C',
          id: exportEntry.exportId,
          filename: exportEntry.fileName,
          exportType: exportEntry.exportTypeName,
          version: entry.year + exportEntry.versionSuffix,
          createdAt: exportEntry.createdAt ? new Date(exportEntry.createdAt) : null,
        })),
      }));

    return timer(0, 60 * 1000).pipe(switchMap(SubmissionsApi.getAll), map(mapper));
  }

  public getTimeDimensionVersions(name: string): Promise<ApiTimeDimensionVersion[]> {
    const sorting = (a: ApiTimeDimensionVersion, b: ApiTimeDimensionVersion): number => {
      if (a.name.endsWith('~')) {
        return 1;
      } else if (b.name.endsWith('~')) {
        return -1;
      } else {
        return a.name.localeCompare(b.name);
      }
    };
    return dimensionsApi.getAllTimeDimensionVersions().then((allVersions) => {
      const timeDimension = allVersions.find((timeDimensions) => timeDimensions.timeDimensionName === name);

      return timeDimension ? timeDimension.versions.sort(sorting) : [];
    });
  }

  public async initiateExport(
    timeDimension: string,
    version: string,
    exportType: number,
    partial: object = {},
    legacyMode?: boolean,
  ): Promise<void> {
    await SubmissionsApi.create({
      version: timeDimension + version,
      type: exportType,
      filter: partial,
      legacyMode: legacyMode,
    });
  }

  public async deleteExport(exportId: number): Promise<void> {
    await SubmissionsApi.delete(exportId);
  }
}
export const ExportService = new ExportServiceClass();
